import { useState, useEffect } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { AppLayout } from '../../components/layouts/AppLayout'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import {
  ArrowLeft,
  Spinner,
  Package,
  MapPin,
  Calendar,
  FileText,
  CheckCircle,
  XCircle,
  PencilSimple,
  Play,
  Prohibit,
  User,
  CaretRight,
  CaretDown,
  Handshake,
  PaperPlaneTilt,
  Clock,
  Check,
  X,
  DownloadSimple,
  ArrowsClockwise,
  Copy
} from '@phosphor-icons/react'
import toast from 'react-hot-toast'
import api from '../../services/api'
import { useDialog } from '../../context/DialogContext'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { formatDateBR, formatDateTimeBR } from '../../lib/date'
import { useExchangeRate, formatCurrency as formatCurrencyUtil } from '../../hooks/useExchangeRate'
import { MultiCurrencyTotal } from '../../components/offer/MultiCurrencyTotal'
import { RoutingInfo } from '../../components/offer/RoutingInfo'
import { totalsFromLegacyOffer } from '../../utils/offerTotals'
import { AgentReputation } from '../../components/AgentReputation'
import { consolidateBreakdown } from '../../utils/breakdownConsolidation'
import { computeCargoMetrics } from '../../utils/cargoMetrics'
import { DocumentAttachments } from '../../components/DocumentAttachments'
import { FEE_UNIT_MAP, computeFeeSubtotal } from '../../constants/feeUnits'
import { generateOfferPdf, type OfferExportData } from '../../utils/offerPdfCanvas'

interface Quote {
  id: string
  txControl: string
  status: string
  statusReason: string | null
  year: number
  incoterm: string
  origin: {
    location: { city: string; state?: string; country: string; zip_code?: string }
    contact_name?: string
    contact_phone?: string
    collect_date?: string
  }
  destination: {
    location: { city: string; state?: string; country: string; zip_code?: string }
    contact_name?: string
    contact_phone?: string
    delivery_date?: string
  }
  pickupLocation?: {
    location: { city: string; state?: string; country: string; zip_code?: string }
  }
  goodsDescription?: string
  goodsSpecial: boolean
  goodsType?: string
  volumeItems?: Array<{
    type: string
    quantity: number
    weight: number
    dimensions?: { length: number; width: number; height: number; unit: string }
    description?: string
  }>
  documents?: Array<{ name: string; url: string; type?: string; key?: string; mimeType?: string; size?: number; uploaded_at?: string; uploaded_by_name?: string }>
  createdAt: string
  updatedAt: string
  modalityId: string
  modalityName: string
  modalityType: string
  volumeType: string
  offersCount: number
  pendingOffersCount: number
  quoteDeadline?: string
  clientReference?: string
  notes?: string
}

interface OfferCondition {
  id: string
  title: string
  content: string
}

interface BreakdownItem {
  id: string
  name: string
  value: number
  category?: string
  currency?: string
  isCustom?: boolean
}

interface Offer {
  id: string
  status: string
  currency: string
  totalValue: number
  totalsByCurrency?: Record<string, number> | null
  freeTimeDays?: number | null
  carrierName?: string | null
  routeType?: 'direct' | 'transshipment' | null
  routeLegs?: Array<{ port: string; country: string; eta?: string | null; etd?: string | null }> | null
  breakdown?: BreakdownItem[] | Record<string, number>
  paymentTerms?: string
  paymentTermDays?: number | null
  deliveryTime?: number
  validityDate?: string
  notes?: string
  conditions?: OfferCondition[]
  documents?: Array<{ name: string; url: string; key?: string; mimeType?: string; size?: number }>
  createdAt: string
  updatedAt: string
  agentName: string
  agentTradingName?: string
  agentLogoUrl?: string
  agentAvgRating?: number
  agentReviewCount?: number
  agentCompletedCount?: number
  freightForwarderId: string
}

type TabType = 'details' | 'offers' | 'documents' | 'timeline'

interface TimelineEvent {
  type: string
  title: string
  detail?: string
  status?: 'sent' | 'failed'
  at: string
}

export default function QuoteDetailsPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { showError, showSuccess, confirm } = useDialog()
  const { rate, preferredCurrency, convertUSDToBRL, convertBRLToUSD, loading: rateLoading } = useExchangeRate({ profileType: 'shipper' })

  // Debug log in development
  if (import.meta.env.DEV) {
    console.log('Exchange rate state:', { rate, preferredCurrency, rateLoading })
  }

  // Get initial tab from URL query param
  const tabParam = searchParams.get('tab')
  const initialTab: TabType = (tabParam === 'offers' || tabParam === 'documents' || tabParam === 'timeline')
    ? tabParam
    : 'details'

  const [loading, setLoading] = useState(true)
  const [quote, setQuote] = useState<Quote | null>(null)
  const [offers, setOffers] = useState<Offer[]>([])
  // Offer list sort/filter in the Ofertas tab (Plane #27)
  const [offerSort, setOfferSort] = useState<'price' | 'transit' | 'payment'>('price')
  const [routeFilter, setRouteFilter] = useState<'all' | 'direct' | 'transshipment'>('all')
  const [activeTab, setActiveTab] = useState<TabType>(initialTab)
  const [timeline, setTimeline] = useState<TimelineEvent[]>([])
  const [timelineLoading, setTimelineLoading] = useState(false)

  useEffect(() => {
    if (activeTab !== 'timeline' || !id) return
    setTimelineLoading(true)
    api.get(`/shipper/quotes/${id}/timeline`)
      .then((r) => setTimeline(r.data.timeline || []))
      .catch(() => setTimeline([]))
      .finally(() => setTimelineLoading(false))
  }, [activeTab, id])
  const [error, setError] = useState<string | null>(null)
  const [actionLoading, setActionLoading] = useState(false)

  // Deadline editing
  const [editingDeadline, setEditingDeadline] = useState(false)
  const [deadlineValue, setDeadlineValue] = useState('')

  // Dialog states
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false)
  const [cancelReason, setCancelReason] = useState('')
  const [cancelLoading, setCancelLoading] = useState(false)
  // Recreate a canceled quote (Plane #22)
  const [recreateDialogOpen, setRecreateDialogOpen] = useState(false)
  const [recreateLoading, setRecreateLoading] = useState(false)

  // Revalidate freight — agent selection modal (Plane #19)
  const [revalidateModalOpen, setRevalidateModalOpen] = useState(false)
  const [revalidateSelected, setRevalidateSelected] = useState<string[]>([])
  const [revalidateLoading, setRevalidateLoading] = useState(false)

  const [acceptDialogOpen, setAcceptDialogOpen] = useState(false)
  const [acceptLoading, setAcceptLoading] = useState(false)
  const [selectedOfferId, setSelectedOfferId] = useState<string | null>(null)
  const [selectedOfferAgent, setSelectedOfferAgent] = useState<string>('')

  const [declineDialogOpen, setDeclineDialogOpen] = useState(false)
  const [declineReason, setDeclineReason] = useState('')
  const [declineLoading, setDeclineLoading] = useState(false)
  const [downloadingOfferId, setDownloadingOfferId] = useState<string | null>(null)

  // State for expanded offer conditions
  const [expandedConditions, setExpandedConditions] = useState<Record<string, boolean>>({})

  // Toggle conditions expansion for a specific offer
  const toggleConditions = (offerId: string) => {
    setExpandedConditions(prev => ({
      ...prev,
      [offerId]: !prev[offerId]
    }))
  }

  // Get initials from name for avatar fallback
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .substring(0, 2)
  }

  useEffect(() => {
    fetchQuoteData()
  }, [id])

  async function fetchQuoteData() {
    try {
      setLoading(true)
      setError(null)

      const [quoteRes, offersRes] = await Promise.all([
        api.get(`/shipper/quotes/${id}`),
        api.get(`/shipper/quotes/${id}/offers`)
      ])

      setQuote(quoteRes.data.quote)
      setOffers(offersRes.data.offers)
    } catch (err: any) {
      console.error('Error fetching quote:', err)
      setError(err.response?.data?.error || 'Erro ao carregar cotação')
    } finally {
      setLoading(false)
    }
  }

  async function handleActivate() {
    if (!quote) return

    const confirmed = await confirm({
      title: 'Ativar Cotação',
      message: 'Ao ativar a cotação, ela será enviada para os agentes de carga receberem propostas. Deseja continuar?',
      variant: 'success',
      confirmText: 'Sim, Ativar',
      cancelText: 'Cancelar'
    })

    if (!confirmed) return

    try {
      setActionLoading(true)
      await api.post(`/shipper/quotes/${id}/activate`)
      showSuccess('Cotação Ativada', 'A cotação foi ativada e os agentes já podem enviar propostas.')
      await fetchQuoteData()
    } catch (err: any) {
      showError('Erro', err.response?.data?.error || 'Erro ao ativar cotação')
    } finally {
      setActionLoading(false)
    }
  }

  async function handleResend() {
    if (!quote) return

    const confirmed = await confirm({
      title: 'Reenviar Cotação',
      message: 'A cotação será reenviada para todos os agentes de carga aprovados. Eles receberão uma nova notificação. Deseja continuar?',
      variant: 'info',
      confirmText: 'Sim, Reenviar',
      cancelText: 'Cancelar'
    })

    if (!confirmed) return

    try {
      setActionLoading(true)
      await api.post(`/shipper/quotes/${id}/resend`)
      showSuccess('Cotação Reenviada', 'A cotação foi reenviada para os agentes de carga.')
    } catch (err: any) {
      showError('Erro', err.response?.data?.error || 'Erro ao reenviar cotação')
    } finally {
      setActionLoading(false)
    }
  }

  // Open the agent-selection modal (Plane #19). Seeds with every agent that
  // currently has an open offer; the shipper can narrow it down or send to all.
  function handleRequestRevalidation() {
    if (!quote) return
    const openAgentIds = Array.from(
      new Set(offers.filter(o => o.status === 'sent').map(o => o.freightForwarderId))
    )
    if (openAgentIds.length === 0) {
      showError('Sem propostas', 'Nenhuma proposta aberta para revalidar.')
      return
    }
    setRevalidateSelected(openAgentIds)
    setRevalidateModalOpen(true)
  }

  async function handleConfirmRevalidation() {
    if (!quote || revalidateSelected.length === 0) return
    try {
      setRevalidateLoading(true)
      await api.post(`/shipper/quotes/${id}/request-revalidation`, {
        freightForwarderIds: revalidateSelected,
      })
      showSuccess('Revalidação Solicitada', 'Os agentes selecionados foram notificados para atualizar o frete.')
      setRevalidateModalOpen(false)
    } catch (err: any) {
      showError('Erro', err.response?.data?.error || 'Erro ao solicitar revalidação')
    } finally {
      setRevalidateLoading(false)
    }
  }

  function openCancelDialog() {
    setCancelReason('')
    setCancelDialogOpen(true)
  }

  async function handleSaveDeadline() {
    if (!quote) return
    try {
      setActionLoading(true)
      await api.patch(`/shipper/quotes/${quote.id}/deadline`, { deadline: deadlineValue || null })
      setQuote({ ...quote, quoteDeadline: deadlineValue || undefined })
      setEditingDeadline(false)
      toast.success(deadlineValue ? 'Prazo atualizado' : 'Prazo removido')
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Erro ao atualizar prazo')
    } finally {
      setActionLoading(false)
    }
  }

  async function handleCancelConfirm() {
    if (!quote) return

    try {
      setCancelLoading(true)
      await api.post(`/shipper/quotes/${id}/cancel`, { reason: cancelReason || undefined })
      setCancelDialogOpen(false)
      showSuccess('Cotação Cancelada', 'A cotação foi cancelada com sucesso.')
      await fetchQuoteData()
    } catch (err: any) {
      showError('Erro', err.response?.data?.error || 'Erro ao cancelar cotação')
    } finally {
      setCancelLoading(false)
    }
  }

  // Recreate a canceled quote (Plane #22). Duplicates the quote (same data,
  // new active quote); "edit" opens the copy in the wizard, "keep" goes
  // straight to the new quote details.
  async function handleRecreate(edit: boolean) {
    if (!quote) return
    try {
      setRecreateLoading(true)
      const res = await api.post(`/quotes/${id}/duplicate`)
      const newQuote = res.data.quote
      setRecreateDialogOpen(false)
      showSuccess('Cotação Recriada', `Nova cotação ${newQuote.tx_control || ''} criada a partir desta.`)
      navigate(edit ? `/shipper/quotes/${newQuote.id}/edit` : `/shipper/quotes/${newQuote.id}`)
    } catch (err: any) {
      showError('Erro', err.response?.data?.error || 'Erro ao recriar cotação')
    } finally {
      setRecreateLoading(false)
    }
  }

  function openAcceptDialog(offerId: string, agentName: string) {
    setSelectedOfferId(offerId)
    setSelectedOfferAgent(agentName)
    setAcceptDialogOpen(true)
  }

  async function handleDownloadOfferPdf(offer: Offer) {
    if (!quote) return
    setDownloadingOfferId(offer.id)
    try {
      // Fetch full offer to get totals BRL/USD, exchange rate, breakdown details, conditions
      const offerRes = await api.get(`/offers/${offer.id}`)
      const fullOffer = offerRes.data.offer || offerRes.data

      const exportData: OfferExportData = {
        id: offer.id,
        status: offer.status,
        currency: offer.currency,
        totalValue: offer.totalValue,
        totalValueBRL: fullOffer.totalValueBRL ?? null,
        totalValueUSD: fullOffer.totalValueUSD ?? null,
        totalsByCurrency: fullOffer.totalsByCurrency ?? null,
        freeTimeDays: fullOffer.freeTimeDays ?? null,
        carrierName: fullOffer.carrierName ?? null,
        routeType: fullOffer.routeType ?? null,
        routeLegs: fullOffer.routeLegs ?? null,
        inputCurrency: fullOffer.inputCurrency ?? null,
        exchangeRateUsed: fullOffer.exchangeRateUsed ?? null,
        exchangeRateSource: fullOffer.exchangeRateSource ?? null,
        breakdown: (offer.breakdown as any) ?? fullOffer.breakdown ?? null,
        paymentTerms: offer.paymentTerms ?? null,
        deliveryTime: offer.deliveryTime ?? null,
        validityDate: offer.validityDate ?? null,
        notes: offer.notes ?? null,
        conditions: offer.conditions ?? fullOffer.conditions ?? null,
        createdAt: offer.createdAt,
        freightForwarderId: offer.freightForwarderId,
        agentName: offer.agentName,
        agentTradingName: offer.agentTradingName,
        agentLogoUrl: offer.agentLogoUrl,
        agentAvgRating: offer.agentAvgRating,
        agentReviewCount: offer.agentReviewCount,
        agentCompletedCount: offer.agentCompletedCount,
        quoteId: quote.id,
        quoteTxControl: quote.txControl || quote.id.slice(0, 8),
        shipperName: (quote as any).shipperName ?? null,
        shipperTradingName: (quote as any).shipperTradingName ?? null,
        shipperCnpj: (quote as any).shipperCnpj ?? null,
        origin: (quote as any).origin ?? null,
        destination: (quote as any).destination ?? null,
        incoterm: (quote as any).incoterm ?? null,
        modalityName: (quote as any).modalityName ?? null,
        modalityType: (quote as any).modalityType ?? null,
        // Quote exposes the volume type as `volumeType` ('FCL'/'LCL'); the PDF's
        // cargo metrics need it as volumeName to count containers (PER_CONTAINER).
        volumeName: (quote as any).volumeType ?? (quote as any).volumeName ?? null,
        goodsDescription: (quote as any).goodsDescription ?? null,
        goodsNcm: (quote as any).goodsNcm ?? null,
        goodsNcmList: (quote as any).goodsNcmList ?? null,
        volumeItems: (quote as any).volumeItems ?? null,
      }

      await generateOfferPdf(exportData)
    } catch (err: any) {
      showError('Erro', err.response?.data?.error || 'Falha ao gerar PDF')
    } finally {
      setDownloadingOfferId(null)
    }
  }

  async function handleAcceptConfirm() {
    if (!selectedOfferId) return

    try {
      setAcceptLoading(true)
      const response = await api.post(`/offers/${selectedOfferId}/accept`)
      setAcceptDialogOpen(false)
      showSuccess('Oferta Aceita', 'A oferta foi aceita e um pedido foi criado!')
      navigate(`/shipper/orders/${response.data.order.id}`)
    } catch (err: any) {
      showError('Erro', err.response?.data?.error || 'Erro ao aceitar oferta')
      await fetchQuoteData()
    } finally {
      setAcceptLoading(false)
    }
  }

  function openDeclineDialog(offerId: string, agentName: string) {
    setSelectedOfferId(offerId)
    setSelectedOfferAgent(agentName)
    setDeclineReason('')
    setDeclineDialogOpen(true)
  }

  async function handleDeclineConfirm() {
    if (!selectedOfferId) return

    try {
      setDeclineLoading(true)
      await api.post(`/offers/${selectedOfferId}/decline`, { reason: declineReason || undefined })
      setDeclineDialogOpen(false)
      setSelectedOfferId(null)
      await fetchQuoteData()
    } catch (err: any) {
      showError('Erro', err.response?.data?.error || 'Erro ao recusar oferta')
    } finally {
      setDeclineLoading(false)
    }
  }

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { label: string; color: string }> = {
      draft: { label: 'Rascunho', color: 'bg-gray-100 text-gray-700' },
      active: { label: 'Ativa', color: 'bg-blue-100 text-blue-700' },
      in_progress: { label: 'Em Análise', color: 'bg-yellow-100 text-yellow-700' },
      completed: { label: 'Aprovada', color: 'bg-green-100 text-green-700' },
      canceled: { label: 'Cancelada', color: 'bg-red-100 text-red-700' },
    }
    const config = statusConfig[status] || { label: status, color: 'bg-gray-100 text-gray-700' }
    return (
      <span className={`px-3 py-1 text-sm font-medium rounded-full ${config.color}`}>
        {config.label}
      </span>
    )
  }

  const getOfferStatusBadge = (status: string) => {
    const statusConfig: Record<string, { label: string; color: string }> = {
      draft: { label: 'Rascunho', color: 'bg-gray-100 text-gray-700' },
      sent: { label: 'Enviada', color: 'bg-blue-100 text-blue-700' },
      accepted: { label: 'Aceita', color: 'bg-green-100 text-green-700' },
      declined: { label: 'Recusada', color: 'bg-red-100 text-red-700' },
      expired: { label: 'Expirada', color: 'bg-orange-100 text-orange-700' },
    }
    const config = statusConfig[status] || { label: status, color: 'bg-gray-100 text-gray-700' }
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${config.color}`}>
        {config.label}
      </span>
    )
  }

  // Format currency with preference conversion
  const formatCurrencyWithPreference = (value: number, offerCurrency: string) => {
    // If offer currency matches preference, just format
    if (offerCurrency === preferredCurrency) {
      return formatCurrencyUtil(value, offerCurrency)
    }

    // Convert based on preference (only USD <-> BRL conversion supported)
    if (offerCurrency === 'USD' && preferredCurrency === 'BRL') {
      if (rate) {
        const convertedValue = convertUSDToBRL(value)
        return formatCurrencyUtil(convertedValue, 'BRL')
      }
      // Rate not loaded yet, show original
      return formatCurrencyUtil(value, offerCurrency)
    }
    if (offerCurrency === 'BRL' && preferredCurrency === 'USD') {
      if (rate) {
        const convertedValue = convertBRLToUSD(value)
        return formatCurrencyUtil(convertedValue, 'USD')
      }
      // Rate not loaded yet, show original
      return formatCurrencyUtil(value, offerCurrency)
    }

    // Fallback: show in offer's original currency (EUR, GBP, CNY, etc.)
    return formatCurrencyUtil(value, offerCurrency)
  }

  // Format currency in original currency (no conversion)
  const formatCurrency = (value: number, currency: string) => {
    return formatCurrencyUtil(value, currency)
  }

  // Helper to render breakdown items - supports both array and object formats.
  // Uses the consolidation util so case/accent variations of the same fee
  // (e.g. "Desconsolidação" custom + platform "Desconsolidação") collapse
  // into a single row for the shipper.
  const renderBreakdownItems = (breakdown: BreakdownItem[] | Record<string, number>, currency: string) => {
    if (Array.isArray(breakdown)) {
      const cargo = computeCargoMetrics(quote?.volumeItems, quote?.volumeType)
      const consolidated = consolidateBreakdown(breakdown as any)
      return consolidated.map((item, index) => {
        const unit = (item as any).unit || 'FORFAIT'
        const def = FEE_UNIT_MAP[unit as keyof typeof FEE_UNIT_MAP]
        const subtotal = def ? computeFeeSubtotal(item.value, unit, cargo) : item.value
        const itemCurrency = item.currency || currency
        return (
          <div key={`${item.name}-${index}`} className="flex justify-between text-sm gap-2">
            <span className="text-gray-300 truncate">
              {item.name}
              {def && unit !== 'FORFAIT' && (
                <span className="text-xs text-gray-500 ml-1">
                  ({formatCurrency(item.value, itemCurrency)}{def.shortLabel})
                </span>
              )}
            </span>
            <span className="font-medium whitespace-nowrap">{formatCurrency(subtotal, itemCurrency)}</span>
          </div>
        )
      })
    }
    // Object format (legacy seed data)
    return Object.entries(breakdown).map(([key, value]) => (
      <div key={key} className="flex justify-between text-sm">
        <span className="text-gray-300">{key}</span>
        <span className="font-medium">{formatCurrency(value as number, currency)}</span>
      </div>
    ))
  }

  // Check if breakdown has items
  const hasBreakdownItems = (breakdown?: BreakdownItem[] | Record<string, number>) => {
    if (!breakdown) return false
    if (Array.isArray(breakdown)) return breakdown.length > 0
    return Object.keys(breakdown).length > 0
  }

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center py-24">
          <Spinner size={32} className="animate-spin text-emerald-600" />
          <span className="ml-3 text-gray-500">Carregando cotação...</span>
        </div>
      </AppLayout>
    )
  }

  if (error || !quote) {
    return (
      <AppLayout>
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <p className="text-red-600 mb-4">{error || 'Cotação não encontrada'}</p>
            <Button variant="outline" onClick={() => navigate('/shipper/quotes')}>
              <ArrowLeft size={16} className="mr-2" />
              Voltar para Cotações
            </Button>
          </div>
        </div>
      </AppLayout>
    )
  }

  const cargoMetrics = computeCargoMetrics(quote.volumeItems, quote.volumeType)
  const formatKg = (n: number) =>
    `${n.toLocaleString('pt-BR', { maximumFractionDigits: 2 })} kg`
  const formatM3 = (n: number) =>
    `${n.toLocaleString('pt-BR', { maximumFractionDigits: 3 })} m³`

  // Ofertas tab: apply route filter + sort (Plane #27). Offers missing the
  // sorted field sink to the bottom so a blank value never wins.
  const displayedOffers = offers
    .filter((o) => routeFilter === 'all' || o.routeType === routeFilter)
    .slice()
    .sort((a, b) => {
      if (offerSort === 'transit') {
        const av = a.deliveryTime ?? Infinity
        const bv = b.deliveryTime ?? Infinity
        return av - bv // lowest transit first
      }
      if (offerSort === 'payment') {
        const av = a.paymentTermDays ?? -Infinity
        const bv = b.paymentTermDays ?? -Infinity
        return bv - av // highest payment term first
      }
      const av = a.totalValue ?? Infinity
      const bv = b.totalValue ?? Infinity
      return av - bv // lowest price first
    })

  // Agents with an open offer — the revalidation targets (Plane #19)
  const revalidatableAgents = Array.from(
    new Map(
      offers
        .filter((o) => o.status === 'sent')
        .map((o) => [o.freightForwarderId, { id: o.freightForwarderId, name: o.agentTradingName || o.agentName }])
    ).values()
  )

  return (
    <AppLayout>
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/shipper/quotes')}
              >
                <ArrowLeft size={20} />
              </Button>
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-xl font-bold text-gray-900">
                    {quote.txControl || `#${quote.id.slice(0, 8)}`}
                  </h1>
                  {getStatusBadge(quote.status)}
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  {quote.modalityName} - {quote.modalityType === 'import' ? 'Importação' : 'Exportação'} - {quote.incoterm}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {quote.status === 'draft' && (
                <>
                  <Button
                    variant="outline"
                    onClick={() => navigate(`/shipper/quotes/${id}/edit`)}
                    disabled={actionLoading}
                  >
                    <PencilSimple size={16} className="mr-2" />
                    Editar
                  </Button>
                  <Button
                    onClick={handleActivate}
                    disabled={actionLoading}
                  >
                    <Play size={16} className="mr-2" />
                    Ativar Cotação
                  </Button>
                </>
              )}
              {(quote.status === 'active' || quote.status === 'in_progress') && (
                <Button
                  variant="outline"
                  onClick={handleResend}
                  disabled={actionLoading}
                >
                  <PaperPlaneTilt size={16} className="mr-2" />
                  Reenviar
                </Button>
              )}
              {(quote.status === 'active' || quote.status === 'in_progress') && quote.offersCount > 0 && (
                <Button
                  variant="outline"
                  onClick={handleRequestRevalidation}
                  disabled={actionLoading}
                  className="text-sky-700 border-sky-200 hover:bg-sky-50"
                >
                  <ArrowsClockwise size={16} className="mr-2" />
                  Revalidar Frete
                </Button>
              )}
              {quote.status !== 'completed' && quote.status !== 'canceled' && (
                <Button
                  variant="destructive"
                  onClick={openCancelDialog}
                  disabled={actionLoading}
                >
                  <Prohibit size={16} className="mr-2" />
                  Cancelar
                </Button>
              )}
              {quote.status === 'canceled' && (
                <Button
                  onClick={() => setRecreateDialogOpen(true)}
                  disabled={actionLoading}
                >
                  <ArrowsClockwise size={16} className="mr-2" />
                  Recriar Cotação
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            {[
              { id: 'details', label: 'Detalhes', icon: Package },
              { id: 'offers', label: `Ofertas (${offers.length})`, icon: FileText },
              { id: 'documents', label: 'Documentos', icon: FileText },
              { id: 'timeline', label: 'Timeline', icon: Clock },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as TabType)}
                className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-emerald-500 text-emerald-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon size={18} />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Details Tab */}
        {activeTab === 'details' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Origin */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center gap-2 mb-4">
                <MapPin size={20} className="text-emerald-600" />
                <h3 className="font-semibold text-gray-900">Origem</h3>
              </div>
              <div className="space-y-2 text-sm">
                <p className="font-medium">
                  {quote.origin.location.city}, {quote.origin.location.country}
                </p>
                {quote.origin.location.state && (
                  <p className="text-gray-500">{quote.origin.location.state}</p>
                )}
                {quote.origin.contact_name && (
                  <p className="text-gray-600">
                    <User size={14} className="inline mr-1" />
                    {quote.origin.contact_name}
                  </p>
                )}
                {quote.origin.collect_date && (
                  <p className="text-gray-600">
                    <Calendar size={14} className="inline mr-1" />
                    Coleta: {formatDateBR(quote.origin.collect_date)}
                  </p>
                )}
              </div>
            </div>

            {/* Destination */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center gap-2 mb-4">
                <MapPin size={20} className="text-red-500" />
                <h3 className="font-semibold text-gray-900">Destino</h3>
              </div>
              <div className="space-y-2 text-sm">
                <p className="font-medium">
                  {quote.destination.location.city}, {quote.destination.location.country}
                </p>
                {quote.destination.location.state && (
                  <p className="text-gray-500">{quote.destination.location.state}</p>
                )}
                {quote.destination.contact_name && (
                  <p className="text-gray-600">
                    <User size={14} className="inline mr-1" />
                    {quote.destination.contact_name}
                  </p>
                )}
                {quote.destination.delivery_date && (
                  <p className="text-gray-600">
                    <Calendar size={14} className="inline mr-1" />
                    Entrega: {formatDateBR(quote.destination.delivery_date)}
                  </p>
                )}
              </div>
            </div>

            {/* Cargo Info */}
            <div className="bg-white rounded-lg border border-gray-200 p-6 lg:col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <Package size={20} className="text-blue-600" />
                <h3 className="font-semibold text-gray-900">Carga</h3>
              </div>
              {quote.goodsDescription && (
                <p className="text-sm text-gray-600 mb-4">{quote.goodsDescription}</p>
              )}
              <div className="flex flex-wrap gap-2 mb-4">
                {quote.goodsType && (
                  <span className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded">
                    {quote.goodsType === 'geral' ? 'Carga Geral' : quote.goodsType === 'empilhavel' ? 'Empilhável' : 'Perigosa'}
                  </span>
                )}
                {quote.goodsSpecial && (
                  <span className="px-2 py-1 text-xs bg-yellow-100 text-yellow-700 rounded">
                    Manuseio Especial
                  </span>
                )}
                {quote.volumeType && (
                  <span className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded">
                    {quote.volumeType}
                  </span>
                )}
              </div>

              {quote.volumeItems && quote.volumeItems.length > 0 && (
                <div className="border-t border-gray-100 pt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Volumes</h4>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-100">
                          <th className="text-left py-2 font-medium text-gray-500">Tipo</th>
                          <th className="text-left py-2 font-medium text-gray-500">Qtd</th>
                          <th className="text-left py-2 font-medium text-gray-500">Peso</th>
                          <th className="text-left py-2 font-medium text-gray-500">Dimensões</th>
                        </tr>
                      </thead>
                      <tbody>
                        {quote.volumeItems.map((item, idx) => (
                          <tr key={idx} className="border-b border-gray-50">
                            <td className="py-2">{item.type}</td>
                            <td className="py-2">{item.quantity}</td>
                            <td className="py-2">{item.weight} kg</td>
                            <td className="py-2">
                              {item.dimensions
                                ? `${item.dimensions.length}x${item.dimensions.width}x${item.dimensions.height} ${item.dimensions.unit}`
                                : '-'
                              }
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="mt-3 pt-3 border-t border-gray-100 flex flex-wrap gap-x-8 gap-y-2 text-sm">
                    <div>
                      <span className="text-gray-500">Peso Total: </span>
                      <span className="font-medium text-gray-900">{formatKg(cargoMetrics.totalWeightKg)}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Cubagem Total: </span>
                      <span className="font-medium text-gray-900">{formatM3(cargoMetrics.totalCubicMeters)}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Observações */}
            {quote.notes && (
              <div className="bg-white rounded-lg border border-gray-200 p-6 lg:col-span-2">
                <h3 className="font-semibold text-gray-900 mb-2">Observações</h3>
                <p className="text-sm text-gray-600 whitespace-pre-wrap">{quote.notes}</p>
              </div>
            )}

            {/* Meta Info */}
            <div className="bg-white rounded-lg border border-gray-200 p-6 lg:col-span-2">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Criada em</p>
                  <p className="font-medium">{formatDateBR(quote.createdAt)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Atualizada em</p>
                  <p className="font-medium">{formatDateBR(quote.updatedAt)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Ofertas Recebidas</p>
                  <p className="font-medium">{quote.offersCount}</p>
                </div>
                <div>
                  <p className="text-gray-500">Ofertas Pendentes</p>
                  <p className="font-medium">{quote.pendingOffersCount}</p>
                </div>
                <div>
                  <p className="text-gray-500 flex items-center gap-1">
                    <Clock size={14} />
                    Prazo para Propostas
                  </p>
                  {editingDeadline ? (
                    <div className="flex items-center gap-1 mt-1">
                      <input
                        type="date"
                        value={deadlineValue}
                        onChange={(e) => setDeadlineValue(e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        min={new Date().toISOString().split('T')[0]}
                      />
                      <button
                        onClick={handleSaveDeadline}
                        disabled={actionLoading}
                        className="p-1 text-emerald-600 hover:text-emerald-700"
                        title="Salvar"
                      >
                        <Check size={16} weight="bold" />
                      </button>
                      <button
                        onClick={() => setEditingDeadline(false)}
                        className="p-1 text-gray-400 hover:text-gray-600"
                        title="Cancelar"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1">
                      <p className={`font-medium ${
                        quote.quoteDeadline && new Date(quote.quoteDeadline) < new Date()
                          ? 'text-red-600'
                          : ''
                      }`}>
                        {quote.quoteDeadline ? formatDateBR(quote.quoteDeadline) : 'Sem prazo'}
                      </p>
                      {quote.status !== 'completed' && quote.status !== 'canceled' && (
                        <button
                          onClick={() => {
                            setDeadlineValue(quote.quoteDeadline ? quote.quoteDeadline.split('T')[0] : '')
                            setEditingDeadline(true)
                          }}
                          className="p-1 text-gray-400 hover:text-emerald-600"
                          title="Alterar prazo"
                        >
                          <PencilSimple size={14} />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Offers Tab */}
        {activeTab === 'offers' && (
          <div className="space-y-4">
            {offers.length === 0 ? (
              <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
                <FileText size={48} className="mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500 mb-2">Nenhuma oferta recebida ainda</p>
                {quote.status === 'draft' && (
                  <p className="text-sm text-gray-400">
                    Ative a cotação para que os agentes possam enviar ofertas
                  </p>
                )}
              </div>
            ) : (
              <>
              {/* Sort + route filter toolbar (Plane #27) */}
              <div className="flex flex-wrap items-center gap-3 bg-white rounded-lg border border-gray-200 px-4 py-3">
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-600">Ordenar por:</label>
                  <select
                    value={offerSort}
                    onChange={(e) => setOfferSort(e.target.value as typeof offerSort)}
                    className="text-sm border border-gray-300 rounded-md px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    <option value="price">Melhor preço</option>
                    <option value="transit">Menor transit time</option>
                    <option value="payment">Maior prazo de pagamento</option>
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-600">Rota:</label>
                  <select
                    value={routeFilter}
                    onChange={(e) => setRouteFilter(e.target.value as typeof routeFilter)}
                    className="text-sm border border-gray-300 rounded-md px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    <option value="all">Todas</option>
                    <option value="direct">Rota direta</option>
                    <option value="transshipment">Com transbordo</option>
                  </select>
                </div>
                <span className="text-xs text-gray-400 ml-auto">
                  {displayedOffers.length} de {offers.length} oferta{offers.length !== 1 ? 's' : ''}
                </span>
              </div>
              {displayedOffers.length === 0 ? (
                <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-sm text-gray-500">
                  Nenhuma oferta corresponde ao filtro de rota selecionado.
                </div>
              ) : (
              displayedOffers.map((offer) => (
                <div
                  key={offer.id}
                  className="bg-white rounded-lg border border-gray-200 p-6 hover:border-gray-300 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      {/* Agent Header with Logo */}
                      <div className="flex items-center gap-4 mb-4">
                        {/* Agent Logo or Initials */}
                        {offer.agentLogoUrl ? (
                          <img
                            src={offer.agentLogoUrl}
                            alt={offer.agentName}
                            className="w-12 h-12 rounded-lg object-cover border border-gray-200"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center text-white font-semibold text-sm">
                            {getInitials(offer.agentTradingName || offer.agentName)}
                          </div>
                        )}
                        <div className="flex-1">
                          <div className="flex items-center gap-3 flex-wrap">
                            <button
                              type="button"
                              onClick={() => navigate(`/freight-forwarders/${offer.freightForwarderId}`)}
                              className="font-semibold text-gray-900 hover:text-emerald-700 hover:underline"
                            >
                              {offer.agentTradingName || offer.agentName}
                            </button>
                            {getOfferStatusBadge(offer.status)}
                          </div>
                          <AgentReputation
                            completedCount={offer.agentCompletedCount}
                            avgRating={offer.agentAvgRating}
                            reviewCount={offer.agentReviewCount}
                            size={14}
                            className="mt-1"
                          />
                          {offer.agentTradingName && (
                            <p className="text-sm text-gray-500 mt-0.5">{offer.agentName}</p>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        {/* Value with Tooltip */}
                        <div className="relative group">
                          <p className="text-gray-500">Valor Total</p>
                          <div className="cursor-help">
                            <MultiCurrencyTotal
                              totalsByCurrency={totalsFromLegacyOffer(offer)}
                              variant="compact"
                              align="start"
                            />
                          </div>
                          {/* Breakdown Tooltip */}
                          {hasBreakdownItems(offer.breakdown) && (
                            <div className="absolute left-0 top-full mt-2 z-20 w-64 p-4 bg-gray-900 text-white rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                              <p className="text-xs text-gray-400 uppercase tracking-wide mb-2">Detalhamento</p>
                              <div className="space-y-1.5">
                                {renderBreakdownItems(offer.breakdown!, offer.currency)}
                              </div>
                            </div>
                          )}
                        </div>
                        <div>
                          <p className="text-gray-500">Tempo de Trânsito</p>
                          <p className="font-medium">
                            {offer.deliveryTime ? `${offer.deliveryTime} dias` : '-'}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-500">Validade</p>
                          <p className="font-medium">
                            {offer.validityDate
                              ? formatDateBR(offer.validityDate)
                              : '-'
                            }
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-500">Recebida em</p>
                          <p className="font-medium">
                            {formatDateBR(offer.createdAt)}
                          </p>
                        </div>
                      </div>

                      <div className="mt-3 pt-3 border-t border-gray-100 flex flex-wrap gap-x-8 gap-y-2 text-sm">
                        <div>
                          <span className="text-gray-500">Peso Total: </span>
                          <span className="font-medium text-gray-900">{formatKg(cargoMetrics.totalWeightKg)}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Cubagem Total: </span>
                          <span className="font-medium text-gray-900">{formatM3(cargoMetrics.totalCubicMeters)}</span>
                        </div>
                      </div>

                      {(offer.carrierName || offer.freeTimeDays != null || offer.routeType) && (
                        <div className="mt-3 pt-3 border-t border-gray-100">
                          <RoutingInfo
                            freeTimeDays={offer.freeTimeDays}
                            carrierName={offer.carrierName}
                            routeType={offer.routeType}
                            routeLegs={offer.routeLegs}
                          />
                        </div>
                      )}

                      {(offer.paymentTerms || offer.paymentTermDays != null) && (
                        <div className="mt-3 pt-3 border-t border-gray-100">
                          <p className="text-sm text-gray-500">
                            <span className="font-medium text-gray-700">Pagamento:</span>{' '}
                            {offer.paymentTermDays != null ? `${offer.paymentTermDays} dias` : ''}
                            {offer.paymentTermDays != null && offer.paymentTerms ? ' — ' : ''}
                            {offer.paymentTerms || ''}
                          </p>
                        </div>
                      )}

                      {offer.notes && (
                        <div className="mt-3 pt-3 border-t border-gray-100">
                          <p className="text-sm text-gray-600">{offer.notes}</p>
                        </div>
                      )}

                      {/* Offer documents attached by the agent */}
                      {offer.documents && offer.documents.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-100">
                          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                            Documentos do agente
                          </p>
                          <DocumentAttachments
                            readOnly
                            documents={offer.documents.map((d) => ({
                              name: d.name,
                              url: d.url,
                              key: d.key,
                              mimeType: d.mimeType || '',
                              size: d.size,
                            }))}
                          />
                        </div>
                      )}

                      {/* Collapsible Offer Conditions */}
                      {offer.conditions && offer.conditions.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-100">
                          <button
                            onClick={() => toggleConditions(offer.id)}
                            className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors w-full text-left"
                          >
                            <CaretDown
                              size={16}
                              className={`text-blue-500 transition-transform duration-200 ${
                                expandedConditions[offer.id] ? 'rotate-0' : '-rotate-90'
                              }`}
                            />
                            <FileText size={14} className="text-blue-500" />
                            Condições Comerciais ({offer.conditions.length})
                          </button>
                          {expandedConditions[offer.id] && (
                            <div className="mt-3 space-y-2">
                              {offer.conditions.map((condition) => (
                                <div
                                  key={condition.id}
                                  className="p-3 bg-gray-50 border border-gray-200 rounded-lg"
                                >
                                  <p className="font-medium text-sm text-gray-900 mb-1">{condition.title}</p>
                                  <p className="text-sm text-gray-600 whitespace-pre-wrap">{condition.content}</p>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="flex flex-col gap-2 ml-4">
                      {/* Plane #21: hide accept/decline once the quote is canceled */}
                      {offer.status === 'sent' && quote?.status !== 'canceled' && (
                        <>
                          <Button
                            size="sm"
                            className="gap-1"
                            disabled={actionLoading}
                            onClick={() => openAcceptDialog(offer.id, offer.agentTradingName || offer.agentName)}
                          >
                            <CheckCircle size={16} />
                            Aceitar
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="gap-1"
                            disabled={actionLoading}
                            onClick={() => openDeclineDialog(offer.id, offer.agentTradingName || offer.agentName)}
                          >
                            <XCircle size={16} />
                            Recusar
                          </Button>
                        </>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-1"
                        disabled={downloadingOfferId === offer.id}
                        onClick={() => handleDownloadOfferPdf(offer)}
                      >
                        <DownloadSimple size={16} />
                        {downloadingOfferId === offer.id ? 'Gerando...' : 'Baixar PDF'}
                      </Button>
                    </div>
                  </div>
                </div>
              ))
              )}
              </>
            )}
          </div>
        )}

        {/* Documents Tab */}
        {activeTab === 'documents' && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <DocumentAttachments
              readOnly
              documents={(quote.documents || []).map((doc) => ({
                name: doc.name,
                url: doc.url,
                key: doc.key,
                mimeType: doc.mimeType || doc.type || '',
                size: doc.size,
                uploaded_at: doc.uploaded_at,
                uploaded_by_name: doc.uploaded_by_name,
              }))}
              emptyText="Nenhum documento anexado"
            />
          </div>
        )}

        {/* Timeline Tab */}
        {activeTab === 'timeline' && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            {timelineLoading ? (
              <div className="text-center text-gray-500 py-10">Carregando timeline...</div>
            ) : timeline.length === 0 ? (
              <div className="text-center py-12">
                <Clock size={48} className="mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">Nenhum evento registrado ainda</p>
              </div>
            ) : (
              <ol className="relative border-l border-gray-200 ml-2">
                {timeline.map((ev, idx) => {
                  const isEmail = ev.type === 'email'
                  const dot =
                    ev.status === 'failed' ? 'bg-red-500'
                    : ev.type === 'offer_accepted' ? 'bg-emerald-500'
                    : ev.type === 'offer_declined' ? 'bg-red-400'
                    : ev.type === 'quote_created' ? 'bg-gray-400'
                    : isEmail ? 'bg-sky-400'
                    : 'bg-emerald-400'
                  return (
                    <li key={idx} className="mb-5 ml-5">
                      <span className={`absolute -left-[7px] w-3 h-3 rounded-full ${dot} border-2 border-white`} />
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-gray-900">{ev.title}</span>
                        {isEmail && (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded ${ev.status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>
                            {ev.status === 'failed' ? 'falhou' : 'enviado'}
                          </span>
                        )}
                      </div>
                      {ev.detail && <p className="text-xs text-gray-500 mt-0.5">{ev.detail}</p>}
                      <p className="text-[11px] text-gray-400 mt-0.5">{formatDateTimeBR(ev.at)}</p>
                    </li>
                  )
                })}
              </ol>
            )}
          </div>
        )}

      </main>

      {/* Cancel Quote Dialog */}
      <ConfirmDialog
        isOpen={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        onConfirm={handleCancelConfirm}
        title="Cancelar Cotação"
        message={
          <div className="space-y-4">
            <p>Tem certeza que deseja cancelar esta cotação? Os agentes de carga não poderão mais enviar propostas.</p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Motivo do cancelamento (opcional)
              </label>
              <Input
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Ex: Mudança de estratégia, projeto cancelado..."
              />
            </div>
          </div>
        }
        variant="warning"
        confirmText="Sim, Cancelar"
        cancelText="Voltar"
        isLoading={cancelLoading}
        icon={<XCircle size={32} weight="duotone" />}
      />

      {/* Recreate Quote Dialog (Plane #22) */}
      {recreateDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-11 h-11 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center">
                <ArrowsClockwise size={24} weight="duotone" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Recriar Cotação</h3>
            </div>
            <p className="text-sm text-gray-600 mb-5">
              Uma nova cotação será criada com os mesmos dados de <strong>{quote.txControl}</strong>.
              Você quer editar as informações antes de enviar, ou manter o original?
            </p>
            <div className="flex flex-col gap-2">
              <Button onClick={() => handleRecreate(true)} disabled={recreateLoading} variant="outline">
                <PencilSimple size={16} className="mr-2" />
                Editar informações
              </Button>
              <Button onClick={() => handleRecreate(false)} disabled={recreateLoading}>
                <Copy size={16} className="mr-2" />
                Manter original
              </Button>
              <button
                onClick={() => setRecreateDialogOpen(false)}
                disabled={recreateLoading}
                className="text-sm text-gray-500 hover:text-gray-700 mt-1"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Request Revalidation Dialog (Plane #19) */}
      {revalidateModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-11 h-11 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center">
                <ArrowsClockwise size={24} weight="duotone" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Revalidar Frete</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Selecione os agentes que receberão o pedido de revalidação (email + notificação).
            </p>
            <label className="flex items-center gap-2 pb-2 mb-2 border-b border-gray-100 cursor-pointer">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                checked={revalidatableAgents.length > 0 && revalidateSelected.length === revalidatableAgents.length}
                onChange={(e) =>
                  setRevalidateSelected(e.target.checked ? revalidatableAgents.map((a) => a.id) : [])
                }
              />
              <span className="text-sm font-medium text-gray-900">Marcar todos</span>
            </label>
            <div className="max-h-56 overflow-y-auto flex flex-col gap-1 mb-5">
              {revalidatableAgents.map((a) => (
                <label
                  key={a.id}
                  className="flex items-center gap-2 py-1.5 px-1 rounded hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                    checked={revalidateSelected.includes(a.id)}
                    onChange={(e) =>
                      setRevalidateSelected((prev) =>
                        e.target.checked ? [...prev, a.id] : prev.filter((x) => x !== a.id)
                      )
                    }
                  />
                  <span className="text-sm text-gray-700">{a.name}</span>
                </label>
              ))}
            </div>
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => setRevalidateModalOpen(false)}
                disabled={revalidateLoading}
                className="text-sm text-gray-500 hover:text-gray-700 px-3 py-2"
              >
                Cancelar
              </button>
              <Button
                onClick={handleConfirmRevalidation}
                disabled={revalidateLoading || revalidateSelected.length === 0}
              >
                Enviar ({revalidateSelected.length})
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Accept Offer Dialog */}
      <ConfirmDialog
        isOpen={acceptDialogOpen}
        onClose={() => setAcceptDialogOpen(false)}
        onConfirm={handleAcceptConfirm}
        title="Aceitar Oferta"
        message={
          <div className="space-y-2">
            <p>Você está prestes a aceitar a oferta de <strong>{selectedOfferAgent}</strong>.</p>
            <p className="text-sm text-gray-500">As demais ofertas serão automaticamente recusadas e um pedido será criado.</p>
          </div>
        }
        variant="success"
        confirmText="Aceitar Oferta"
        cancelText="Cancelar"
        isLoading={acceptLoading}
        icon={<Handshake size={32} weight="duotone" />}
      />

      {/* Decline Offer Dialog */}
      <ConfirmDialog
        isOpen={declineDialogOpen}
        onClose={() => setDeclineDialogOpen(false)}
        onConfirm={handleDeclineConfirm}
        title="Recusar Oferta"
        message={
          <div className="space-y-4">
            <p>Tem certeza que deseja recusar a oferta de <strong>{selectedOfferAgent}</strong>?</p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Motivo da recusa (opcional)
              </label>
              <Input
                value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
                placeholder="Ex: Valor acima do orçamento, prazo incompatível..."
              />
            </div>
          </div>
        }
        variant="danger"
        confirmText="Recusar Oferta"
        cancelText="Voltar"
        isLoading={declineLoading}
      />
    </AppLayout>
  )
}
